"use strict";
// SPDX-FileCopyrightText: 2024 Red Bee Media Ltd <https://www.redbeemedia.com/>
//
// SPDX-License-Identifier: MIT
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.HlsJs = void 0;
const hls_js_1 = __importStar(require("hls.js"));
const js_player_shared_1 = require("@ericssonbroadcastservices/js-player-shared");
const common_1 = require("../device/common");
const AbstractBaseEngine_1 = require("./AbstractBaseEngine");
const defaultConfigurations_1 = require("./defaultConfigurations");
const interfaces_1 = require("./interfaces");
const restrictions_1 = require("./utils/restrictions");
const LIVE_EDGE_TOLERANCE = 6; // seconds
function getErrorType({ type, details }) {
    switch (type) {
        case hls_js_1.ErrorTypes.NETWORK_ERROR:
            return js_player_shared_1.ErrorTypes.NETWORK;
        case hls_js_1.ErrorTypes.MEDIA_ERROR:
            return (details === null || details === void 0 ? void 0 : details.includes("CODEC")) ? js_player_shared_1.ErrorTypes.CODEC : js_player_shared_1.ErrorTypes.MEDIA;
        case hls_js_1.ErrorTypes.MUX_ERROR:
        case hls_js_1.ErrorTypes.OTHER_ERROR:
            return js_player_shared_1.ErrorTypes.PLAYER_ENGINE;
        default:
            return js_player_shared_1.ErrorTypes.OTHER;
    }
}
function createTrack(track) {
    var _a;
    let kind;
    const characteristics = (_a = track.attrs) === null || _a === void 0 ? void 0 : _a.CHARACTERISTICS;
    if (track.type === "AUDIO") {
        if (!characteristics) {
            kind = "main";
        }
        else if (characteristics.includes("accessibility")) {
            kind = "description";
        }
        else {
            // default to 'alternative' if we cannot find any matching characteristic.
            // (This should almost never happen with normal streams/tracks)
            kind = "alternative";
        }
    }
    if (["SUBTITLES", "CLOSED-CAPTIONS"].includes(track.type)) {
        if (track.forced) {
            kind = "forced";
        }
        else if (!characteristics) {
            kind = "subtitles";
        }
        else if (characteristics.includes("transcribes") ||
            characteristics.includes("describes")) {
            // Either 'public.accessibility.transcribes-spoken-dialog' or 'public.accessibility.describes-music-and-sound' (can also be both)
            kind = "captions";
        }
        else {
            // default to an empty string if we cannot find any matching characteristic.
            // (This should almost never happen with normal streams/tracks)
            kind = "";
        }
    }
    return {
        id: track.id.toString(),
        language: track.lang,
        label: track.name || (0, js_player_shared_1.getLabel)(track.lang),
        raw: track,
        kind,
    };
}
class HlsJs extends AbstractBaseEngine_1.AbstractBaseEngine {
    constructor(videoElement, instanceSettings) {
        super(videoElement, instanceSettings);
        this.name = "Hls.js";
        this.playertechVersion = hls_js_1.default.version;
        this.currentBitrate = 0;
        this.recoveryAttempts = 0;
        this.maxRecoveryAttempts = 1;
        const configuration = {
            debug: false,
            // TODO: EMP-18317 remove `xhrSetup` once LG/Nowtilus supports https again...
            xhrSetup: (xhr, url) => {
                // 😐🔫 Workaround to allow playback of SSAI streams on LG 2017-2019...
                if ((0, common_1.isLGTV)() && url.includes("redbee.serverside.ai")) {
                    url = url.replace("https://", "http://");
                }
                xhr.open("GET", url, true);
            },
        };
        const customConfiguration = this.instanceSettings.initOptions.customHlsJsConfiguration || {};
        this.hls = new hls_js_1.default(Object.assign(configuration, defaultConfigurations_1.HlsJs, customConfiguration));
        this.hls.subtitleDisplay = false;
        this.setupEventListeners();
    }
    setupEventListeners() {
        super.setupEventListeners();
        this.hls.on(hls_js_1.default.Events.FRAG_CHANGED, this.onFragChanged.bind(this));
        this.hls.on(hls_js_1.default.Events.ERROR, this.onHlsError.bind(this));
        this.hls.on(hls_js_1.default.Events.AUDIO_TRACK_SWITCHED, this.onAudioTrackChange.bind(this));
    }
    load({ src, license, startTime, audio, subtitle }) {
        // TODO: fix typing issue when overriding this method
        this.src = src;
        this.hls.loadSource(src);
        this.hls.attachMedia(this.videoElement);
        this.hls.once(hls_js_1.default.Events.MANIFEST_PARSED, () => __awaiter(this, void 0, void 0, function* () {
            var _a;
            this.setResolutionAndBitrateRestrictions(yield (0, restrictions_1.getResolutionAndBitrateRestrictions)((_a = this.instanceSettings.playResponse) === null || _a === void 0 ? void 0 : _a.contractRestrictions));
            this.setContentSpecificConfiguration();
            this.hls.startLoad(startTime === undefined ? -1 : Math.floor(startTime));
        }));
        super.load({
            src,
            license,
            startTime,
            audio,
            subtitle,
        });
    }
    stop() {
        this.hls.detachMedia();
        this.setPlaybackState(js_player_shared_1.PlaybackState.IDLE);
    }
    isLive() {
        return this.videoElement.duration === Infinity;
    }
    isAtLiveEdge() {
        return (this.hls.liveSyncPosition !== null &&
            this.getCurrentTime() >= this.hls.liveSyncPosition - LIVE_EDGE_TOLERANCE);
    }
    setResolutionAndBitrateRestrictions({ minBitrate, maxBitrate, maxResHeight, }) {
        if (!minBitrate && !maxBitrate && !maxResHeight)
            return;
        const levels = this.hls.levels;
        const levelsToRemove = []; // We need to store them here since the index will change if we remove them
        for (const levelIndex in levels) {
            const level = levels[levelIndex];
            if ((minBitrate && level.bitrate < minBitrate) ||
                (maxBitrate && level.bitrate > maxBitrate) ||
                (maxResHeight && level.height > maxResHeight)) {
                levelsToRemove.push(levelIndex);
            }
        }
        const sortedLevels = levelsToRemove.sort().reverse();
        sortedLevels.forEach((levelIdentifier) => {
            this.hls.removeLevel(Number(levelIdentifier));
        });
    }
    setContentSpecificConfiguration() {
        if (!this.instanceSettings.mediaInfo || !this.instanceSettings.formats)
            return;
        const isLive = this.instanceSettings.mediaInfo.isLive;
        if (isLive) {
            const currentPlaybackObject = this.instanceSettings.formats.find((f) => f.mediaLocator === this.src);
            if (currentPlaybackObject === null || currentPlaybackObject === void 0 ? void 0 : currentPlaybackObject.liveDelay) {
                this.hls.config.liveSyncDuration =
                    currentPlaybackObject.liveDelay / 1000;
            }
        }
    }
    setQualityLevel(level) {
        this.hls.nextLevel = level.id;
        this.hls.loadLevel = level.id;
    }
    getQualityLevel() {
        const level = this.hls.levels[this.hls.currentLevel];
        if (level) {
            const qualityLevel = this.getQualityLevels().find((l) => l.bandwidth === level.bitrate);
            if (qualityLevel) {
                return qualityLevel;
            }
        }
        return super.getQualityLevel();
    }
    getQualityLevels() {
        if (!this.hls.levels || !Array.isArray(this.hls.levels))
            return [];
        const qualityLevels = this.hls.levels.map((level) => {
            const index = this.hls.levels.findIndex((l) => l.bitrate === level.bitrate);
            const framerate = level.attrs["FRAME-RATE"];
            const qualityLevel = {
                id: index,
                bandwidth: level.bitrate,
                width: level.width,
                height: level.height,
                framerate: framerate ? parseInt(framerate, 10) : undefined,
            };
            qualityLevel.name = (0, AbstractBaseEngine_1.getQualityLevelName)(qualityLevel);
            return qualityLevel;
        });
        return [...super.getQualityLevels(), ...qualityLevels];
    }
    setSubtitleTrack(track) {
        const tracks = this.hls.subtitleTracks;
        const index = tracks.findIndex((t) => track && t.id === Number(track.id));
        this.hls.subtitleDisplay = index !== -1;
        this.hls.subtitleTrack = -1; // ugly hack, without this the first attempt to set track won't work in HlsJs
        // Note: Hls.js also activate tracks with the same "groupId" ("GROUP-ID" in the manifest) when you set a track (this is a feature)
        this.hls.subtitleTrack = index;
        // Visibility needs to be set to hidden for all tracks that are set to showing. This is due to: https://github.com/video-dev/hls.js/issues/4530
        Array.from(this.videoElement.textTracks || []).forEach((track) => {
            if (track.mode === "showing") {
                track.mode = "hidden";
            }
        });
    }
    getSubtitleTrack() {
        if (this.hls.subtitleTrack !== -1) {
            if (this.hls.subtitleDisplay) {
                const track = this.hls.subtitleTracks[this.hls.subtitleTrack];
                if (track) {
                    return createTrack(track);
                }
            }
        }
    }
    getSubtitleTracks() {
        const tracks = this.hls.subtitleTracks;
        return this.filterVisibleSubtitleTracks(tracks
            .map((track) => {
            return createTrack(track);
        })
            .filter((track, index, array) => array.findIndex((compTrack) => track.language === compTrack.language &&
            track.id === compTrack.id &&
            track.kind === compTrack.kind) === index));
    }
    setAudioTrack(track) {
        if (track) {
            const audioTrack = this.hls.audioTracks.find((audioTrack) => Number(audioTrack.id) === Number(track.id));
            if (audioTrack) {
                this.hls.audioTrack = Number(audioTrack.id);
            }
        }
    }
    getAudioTrack() {
        const audioTrack = this.hls.audioTracks.find((audioTrack) => audioTrack.id === this.hls.audioTrack);
        if (audioTrack) {
            return createTrack(audioTrack);
        }
    }
    getAudioTracks() {
        const activeTrack = this.hls.audioTracks.find((audioTrack) => audioTrack.id === this.hls.audioTrack);
        return (this.hls.audioTracks
            // filter out audioTracks that is not the same group as the running track
            .filter((audioTrack) => audioTrack.groupId === (activeTrack === null || activeTrack === void 0 ? void 0 : activeTrack.groupId))
            .map((audioTrack) => createTrack(audioTrack))
            .filter((track, index, array) => array.findIndex((compTrack) => track.language === compTrack.language &&
            track.kind === compTrack.kind &&
            track.label === compTrack.label) === index));
    }
    onFragChanged(_, data) {
        const tags = data.frag.tagList;
        const level = this.hls.levels[data.frag.level];
        const bitrate = level.bitrate;
        if (bitrate !== this.currentBitrate) {
            this.emit(interfaces_1.EngineEvents.BITRATE_CHANGED, {
                bitrate,
                width: level.width,
                height: level.height,
            });
            this.currentBitrate = bitrate;
        }
        if (!this.utcStartTime && data.frag.programDateTime) {
            this.setUTCStartTime(data.frag.programDateTime - data.frag.start * 1000);
        }
        if (tags) {
            tags.forEach((tag) => {
                if (tag.includes("EXT-X-DATERANGE")) {
                    const metadataEvent = {
                        event: tag,
                        engineName: "Hls.js",
                        engineVersion: hls_js_1.default.version,
                    };
                    this.emit(interfaces_1.EngineEvents.METADATA_EVENT, metadataEvent);
                }
            });
        }
    }
    onHlsError(_event, data) {
        if (data.fatal) {
            const playerError = new js_player_shared_1.HlsJsPlayerError(data.details, {
                type: getErrorType(data),
            });
            if (this.recoveryAttempts >= this.maxRecoveryAttempts) {
                this.emit(interfaces_1.EngineEvents.ERROR, playerError);
                return;
            }
            this.recoveryAttempts += 1;
            switch (data.type) {
                case hls_js_1.default.ErrorTypes.MEDIA_ERROR:
                    console.warn("fatal media error encountered, try to recover");
                    this.hls.recoverMediaError();
                    break;
                default:
                    this.emit(interfaces_1.EngineEvents.ERROR, playerError);
                    break;
            }
        }
    }
    onError() {
        // no-op, handled by onHlsError
    }
    getSeekable() {
        var _a;
        // Use the same method as hls.js uses to determine seekable range
        // https://github.com/video-dev/hls.js/blob/master/src/controller/buffer-controller.ts#L855
        // On older browsers the mediasource seekable cannot be updated so we have to get the range manually.
        const level = (_a = this.hls.levels) === null || _a === void 0 ? void 0 : _a.find((level) => level.id === this.hls.loadLevel);
        const levelDetails = level === null || level === void 0 ? void 0 : level.details;
        if (levelDetails === null || levelDetails === void 0 ? void 0 : levelDetails.fragments) {
            const fragments = levelDetails === null || levelDetails === void 0 ? void 0 : levelDetails.fragments;
            const start = Math.max(0, fragments[0].start);
            const end = Math.max(start, start + levelDetails.totalduration);
            return {
                start,
                end,
            };
        }
        return super.getSeekable();
    }
    seekTo(pos) {
        // hls.js can fail to seek to a non integer position.
        super.seekTo(Math.round(pos));
    }
    seekToLive() {
        const livePosition = this.hls.liveSyncPosition;
        if (livePosition) {
            this.videoElement.currentTime = livePosition;
        }
    }
    destroy() {
        if (this.hls) {
            this.hls.destroy();
        }
        super.destroy();
    }
}
exports.HlsJs = HlsJs;
//# sourceMappingURL=HlsJs.js.map